import { DOCUMENT, effect, inject, Injectable, RendererFactory2 } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import { select, Store } from '@ngxs/store';
import { get } from 'lodash-es';
import { Subscription } from 'rxjs';

import { ResetMessages, ToastSelectors } from '@efficiently/client-core-toast';
import { APP_TOAST } from '@efficiently/client-util-constants';

import { ToastsComponent } from './toasts.component';

@Injectable({ providedIn: 'root' })
export class ToastsService {
	private readonly store = inject(Store);
	private readonly snackBar = inject(MatSnackBar);
	private readonly document = inject(DOCUMENT);
	private readonly renderer = inject(RendererFactory2).createRenderer(null, null);

	private readonly snackbarContainerNativePath = 'containerInstance._elementRef.nativeElement';
	private readonly appToastOverlayClass: string = 'ds-toast-overlay';

	private readonly messages = select(ToastSelectors.messages);

	private appToastOverlay: HTMLElement;

	private snackBarRef: MatSnackBarRef<ToastsComponent> | null = null;
	private snackBarSubscription: Subscription;

	constructor() {
		effect(() => {
			if (this.messages()?.length) {
				this.openSnackBar();
			} else {
				this.closeSnackBar();
			}
		});
	}

	public setToastOverlay(): void {
		this.appToastOverlay = this.document.getElementById(APP_TOAST);
	}

	public openSnackBar(): void {
		if (!this.snackBarRef) {
			this.snackBarRef = this.snackBar?.openFromComponent(ToastsComponent, this.getConfig());
			this.appendAppToast();
			// TODO EFF-17197 - If this task succeeds in separating this MatSnackBar from the others in the project, then can remove this subscription
			this.snackBarSubscription = this.snackBarRef.afterDismissed().subscribe(() => {
				this.store.dispatch(new ResetMessages());
				this.removeAppToast();
				this.snackBarRef = null;
			});
		}
	}

	public closeSnackBar(): void {
		if (this.snackBarRef) {
			this.snackBarSubscription.unsubscribe();
			this.removeAppToast();
			this.snackBarRef.dismiss();
			this.snackBarRef = null;
		}
	}

	private getConfig(): MatSnackBarConfig {
		const config: MatSnackBarConfig = new MatSnackBarConfig();
		config.verticalPosition = 'top';
		config.politeness = 'assertive';
		return config;
	}

	private appendAppToast(): void {
		if (this.appToastOverlay && this.snackBarRef) {
			this.renderer.appendChild(this.appToastOverlay, get(this.snackBarRef, this.snackbarContainerNativePath));
			this.renderer.addClass(this.appToastOverlay, this.appToastOverlayClass);
		}
	}

	private removeAppToast(): void {
		if (this.appToastOverlay && this.snackBarRef) {
			this.renderer.removeChild(this.appToastOverlay, get(this.snackBarRef, this.snackbarContainerNativePath));
			this.renderer.removeClass(this.appToastOverlay, this.appToastOverlayClass);
		}
	}
}
